import { Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';

import type { WorkspaceDBService } from '../../db';
import type { AgentBetaStore } from '../stores/agent-store-beta';
import type { LLMBaseDataSnapshot } from '../types';

const DEFAULT_CONVERSION_ID = '##_DEFAULT_ID';

/**
 * AgentStoreService
 * 
 * 职责：处理 agent-store 数据的所有写操作
 * - 所有写操作直接操作 WorkspaceDB
 * - 通过 DB 的订阅机制自动同步到 AgentBetaStore (只读层)
 * - 自动处理多 tab 同步（基于 YDoc CRDT）
 */
export class AgentStoreService extends Service {
  constructor(
    private readonly workspaceDBService: WorkspaceDBService,
    private readonly agentStore: AgentBetaStore
  ) {
    super();
  }

  /**
   * 创建或更新 agent record
   * ✅ 直接操作 DB，订阅会自动更新 store
   */
  async createRecordByDocId(
    doc_id: string,
    type: 'create' | 'update' | 'complex',
    options: {
      docBaseDataSnapshot: LLMBaseDataSnapshot;
      record_id?: string;
      conversion_id?: string;
      commands?: Array<any>;
      title_new_value?: string;
    }
  ): Promise<void> {
    const record_id = options.record_id || nanoid();
    const commands = options.commands || [];
    const conversion_id = options.conversion_id || DEFAULT_CONVERSION_ID;

    try {
      // 检查是否已存在该 doc_id 的记录
      const existingRecords = this.workspaceDBService.db.agentRecords.find({
        doc_id,
      });

      if (existingRecords.length > 0) {
        // 更新现有记录（每个 doc 只维护一条记录）
        const existingRecord = existingRecords[0];
        this.workspaceDBService.db.agentRecords.update(existingRecord.id, {
          record_id,
          conversion_id,
          type,
          docBaseDataSnapshot: options.docBaseDataSnapshot,
          commands,
          title_new_value: options.title_new_value,
          updatedAt: Date.now(),
        });
      } else {
        // 创建新记录
        this.workspaceDBService.db.agentRecords.create({
          doc_id,
          record_id,
          conversion_id,
          type,
          docBaseDataSnapshot: options.docBaseDataSnapshot,
          commands,
          title_new_value: options.title_new_value,
        });
      }

      // ✅ 不需要手动更新 store，订阅会自动处理
    } catch (error) {
      console.error('Failed to create/update agent record:', error);
      throw error;
    }
  }

  /**
   * 更新 commands
   * 从只读 store 读取当前数据，合并后写入 DB
   * 
   * ⚠️ 注意：如果 store 中暂时找不到 record，会尝试从 DB 直接读取
   * 这可以避免订阅延迟导致的竞态条件
   */
  async updateCommands(params: {
    doc_id: string;
    conversion_id: string;
    commands: Array<any>;
    title_new_value?: string;
  }): Promise<void> {
    const { doc_id, conversion_id, commands, title_new_value } = params;

    try {
      // 优先从只读 store 读取当前数据
      let record = this.agentStore.getRecordByDocId(doc_id);
      
      // ✅ 如果 store 中没有，尝试从 DB 直接读取（处理订阅延迟）
      if (!record) {
        const existingRecords = this.workspaceDBService.db.agentRecords.find({
          doc_id,
        });
        
        if (existingRecords.length === 0) {
          console.warn(`No record found for doc_id: ${doc_id} in both store and DB`);
          return;
        }
        
        // 从 DB 记录构造临时 record
        const dbRecord = existingRecords[0];
        record = {
          docBaseDataSnapshot: dbRecord.docBaseDataSnapshot as any,
          record_id: dbRecord.record_id,
          conversion_id: dbRecord.conversion_id,
          type: dbRecord.type as 'create' | 'update' | 'complex',
          commands: (dbRecord.commands as Array<any>) || [],
          title_new_value: dbRecord.title_new_value || undefined,
        };
      }

      // 如果是默认的 conversion_id，则更新为新的
      let actualConversionId = conversion_id;
      if (record.conversion_id === DEFAULT_CONVERSION_ID) {
        actualConversionId = conversion_id;
      } else if (record.conversion_id !== conversion_id) {
        // conversion_id 不匹配，可能是不同的对话，忽略更新
        console.warn(
          `Conversion ID mismatch: ${record.conversion_id} vs ${conversion_id}`
        );
        return;
      }

      // ✅ 触发 suggestionService 处理命令（保持原有逻辑）
      this.agentStore.triggerSuggestionServiceCommand({
        doc_id,
        conversion_id: actualConversionId,
        commands,
        title_new_value,
      });

      // 🔒 重要：再次从 DB 读取最新数据，避免多 tab 冲突覆盖
      // 获取 DB 中的最新记录
      const existingRecords = this.workspaceDBService.db.agentRecords.find({
        doc_id,
      });

      if (existingRecords.length > 0) {
        const existingRecord = existingRecords[0];
        
        // ✅ 使用 DB 中的最新 commands，而不是 store 中可能过期的数据
        const latestCommands = (existingRecord.commands as Array<any>) || [];
        const updatedCommands = [...latestCommands, ...commands];
        
        this.workspaceDBService.db.agentRecords.update(existingRecord.id, {
          conversion_id: actualConversionId,
          commands: updatedCommands,
          title_new_value: title_new_value || record.title_new_value,
          updatedAt: Date.now(),
        });
      }

      // ✅ 订阅会自动更新 store
    } catch (error) {
      console.error('Failed to update commands:', error);
      throw error;
    }
  }

  /**
   * 更新 docBaseDataSnapshot
   */
  async updateDocBaseDataSnapshot(params: {
    doc_id: string;
    docBaseDataSnapshot: LLMBaseDataSnapshot;
  }): Promise<void> {
    const { doc_id, docBaseDataSnapshot } = params;

    try {
      const existingRecords = this.workspaceDBService.db.agentRecords.find({
        doc_id,
      });

      if (existingRecords.length > 0) {
        const existingRecord = existingRecords[0];
        this.workspaceDBService.db.agentRecords.update(existingRecord.id, {
          docBaseDataSnapshot,
          updatedAt: Date.now(),
        });
      }

      // ✅ 订阅会自动更新 store
    } catch (error) {
      console.error('Failed to update docBaseDataSnapshot:', error);
      throw error;
    }
  }

  /**
   * 删除指定 doc 的 record
   * 通常在 suggestions 创建成功后调用，表示数据已迁移到 ySuggestionInfo
   * 
   * ⚠️ 多 Tab 场景：如果多个 tab 同时处理同一个 doc，可能都会尝试删除
   * 这是安全的，因为 DB 的 delete 操作是幂等的
   */
  async removeRecordByDocId(doc_id: string): Promise<void> {
    try {
      // 找到所有匹配的记录并删除
      const existingRecords = this.workspaceDBService.db.agentRecords.find({
        doc_id,
      });

      if (existingRecords.length === 0) {
        console.log(`[AgentStoreService] Record already deleted for doc_id: ${doc_id}`);
        return;
      }

      for (const record of existingRecords) {
        this.workspaceDBService.db.agentRecords.delete(record.id);
      }

      console.log(`[AgentStoreService] Deleted ${existingRecords.length} record(s) for doc_id: ${doc_id}`);

      // ✅ 订阅会自动从 store 中移除
    } catch (error) {
      console.error('Failed to remove agent record:', error);
      throw error;
    }
  }

  /**
   * 清理所有过期的 records
   * 可选：定期调用以清理可能存在的僵尸数据
   */
  async cleanupExpiredRecords(maxAgeMs: number = 7 * 24 * 60 * 60 * 1000): Promise<void> {
    try {
      const now = Date.now();
      const allRecords = this.workspaceDBService.db.agentRecords.find();

      for (const record of allRecords) {
        const age = now - (record.createdAt || 0);
        if (age > maxAgeMs) {
          this.workspaceDBService.db.agentRecords.delete(record.id);
        }
      }
    } catch (error) {
      console.error('Failed to cleanup expired records:', error);
    }
  }
}

